// Onboarding Page - Create first organization
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  StepConnector,
  stepConnectorClasses,
  styled,
  Avatar,
} from '@mui/material';
import {
  Business,
  Code,
  Celebration,
  CheckCircle,
  BugReport,
  MenuBook,
  Lightbulb,
  SupportAgent,
  ContentCopy,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { doc, setDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '@/services/firebase/config';
import { PLAN_LIMITS, DEFAULT_BRANDING } from '@/types';
import type { Organization } from '@/types';

// Custom styled stepper connector with gradient
const GradientConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#e2e8f0',
    borderRadius: 1,
  },
}));

// Custom step icon component
const GradientStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ ownerState }) => ({
  backgroundColor: '#e2e8f0',
  zIndex: 1,
  color: '#94a3b8',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 0.3s ease',
  ...(ownerState.active && {
    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)',
    color: '#fff',
  }),
  ...(ownerState.completed && {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
  }),
}));

function GradientStepIcon(props: { active?: boolean; completed?: boolean; icon: React.ReactNode; className?: string }) {
  const { active, completed, className, icon } = props;

  const icons: { [index: string]: React.ReactElement } = {
    1: <Business />,
    2: <Code />,
    3: <Celebration />,
  };

  return (
    <GradientStepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? <CheckCircle /> : icons[String(icon)]}
    </GradientStepIconRoot>
  );
}

// Countries sorted by expected SaaS revenue potential
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'IE', name: 'Ireland' },
  { code: 'SG', name: 'Singapore' },
  { code: 'JP', name: 'Japan' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'OTHER', name: 'Other' },
];

const organizationSchema = z.object({
  name: z.string().min(2, 'Organization name must be at least 2 characters'),
  billingCountry: z.string().min(1, 'Please select your country'),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

const steps = ['Create Organization', 'Install Widget', 'Start Collecting'];

export const OnboardingPage = () => {
  const { user, profile, refreshProfile } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createdOrg, setCreatedOrg] = useState<Organization | null>(null);
  const [checkingOrgs, setCheckingOrgs] = useState(true);
  const [copied, setCopied] = useState(false);

  // Form hook must be called unconditionally (before any early returns)
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      billingCountry: '',
    },
  });

  const selectedCountry = watch('billingCountry');
  const isNZBased = selectedCountry === 'NZ';

  // Redirect users who already have organizations - they shouldn't be here
  useEffect(() => {
    if (!profile) {
      // Still loading profile, wait
      return;
    }

    // Check if user already has organizations
    if (profile.organizationIds && profile.organizationIds.length > 0) {
      // User has organizations, redirect to dashboard
      console.log('User already has organizations, redirecting to dashboard');
      navigate('/app/dashboard', { replace: true });
      return;
    }

    // No organizations, allow onboarding
    setCheckingOrgs(false);
  }, [profile, navigate]);

  // Show loading while checking for existing organizations
  if (checkingOrgs) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#3b82f6' }} />
        <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.7)' }}>
          Checking your account...
        </Typography>
      </Box>
    );
  }

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const generateApiKey = (): string => {
    return `ss_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 15)}`;
  };

  const onSubmit = async (data: OrganizationFormData) => {
    if (!user || !profile) return;

    setLoading(true);
    try {
      const orgId = `org_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 9)}`;
      const projectId = `proj_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 9)}`;

      const newOrg: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'> = {
        name: data.name,
        slug: generateSlug(data.name),
        plan: 'free',
        ownerId: user.uid,
        billingEmail: user.email || '',
        billingCountry: data.billingCountry,
        isNZBased,
        limits: PLAN_LIMITS.free,
        branding: DEFAULT_BRANDING,
        usage: {
          projectCount: 1,
          seatCount: 1,
          articleCount: 0,
          ticketCount: 0,
          featureRequestCount: 0,
        },
        settings: {
          defaultLanguage: 'en',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          supportEmail: user.email || '',
          autoReplyEnabled: false,
        },
        createdBy: user.uid,
      };

      // Create organization
      await setDoc(doc(db, 'organizations', orgId), {
        ...newOrg,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Create default project
      await setDoc(doc(db, `organizations/${orgId}/projects`, projectId), {
        name: 'Default Project',
        slug: 'default',
        description: 'Your first project',
        organizationId: orgId,
        widgetSettings: {
          enabled: true,
          position: 'bottom-right',
          primaryColor: '#2563eb',
          launcherIcon: 'chat',
          showKnowledgeBase: true,
          showFeaturePortal: true,
        },
        apiKey: generateApiKey(),
        features: {
          feedbackEnabled: true,
          supportTicketsEnabled: true,
          featureRequestsEnabled: true,
          surveysEnabled: false,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user.uid,
      });

      // Add organization member
      await setDoc(doc(db, `organizations/${orgId}/members`, user.uid), {
        organizationId: orgId,
        userId: user.uid,
        email: user.email,
        displayName: profile.displayName,
        role: 'owner',
        invitedAt: serverTimestamp(),
        acceptedAt: serverTimestamp(),
        status: 'active',
      });

      // Update user's organization list
      await updateDoc(doc(db, 'users', user.uid), {
        organizationIds: arrayUnion(orgId),
        updatedAt: serverTimestamp(),
      });

      // Refresh the profile to get updated organizationIds
      await refreshProfile();

      setCreatedOrg({ id: orgId, ...newOrg } as Organization);
      showSuccess('Organization created successfully!');
      setActiveStep(1);
    } catch (err) {
      console.error('Error creating organization:', err);
      showError('Failed to create organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    // Set flag to prevent redirect loop - AppLayout will clear this once orgs are loaded
    localStorage.setItem('onboardingCompleted', 'true');
    localStorage.setItem('currentOrgId', createdOrg?.id || '');
    // Do a full page navigation to refresh all state with the new organization
    window.location.href = '/app/dashboard';
  };

  const handleCopyCode = () => {
    const code = `<script src="https://widget.supportspace.io/v1/widget.js"
  data-project="${createdOrg?.id || 'PROJECT_ID'}">
</script>`;
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const widgetFeatures = [
    { icon: <BugReport sx={{ color: '#ef4444' }} />, text: 'Submit bug reports and feedback' },
    { icon: <MenuBook sx={{ color: '#3b82f6' }} />, text: 'Search your knowledge base for help articles' },
    { icon: <Lightbulb sx={{ color: '#f59e0b' }} />, text: 'Request new features and vote on existing ones' },
    { icon: <SupportAgent sx={{ color: '#10b981' }} />, text: 'Create support tickets' },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '60%',
          height: '100%',
          background: 'radial-gradient(ellipse, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-30%',
          left: '-10%',
          width: '50%',
          height: '80%',
          background: 'radial-gradient(ellipse, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: { xs: 3, md: 4 },
          }}
        >
          {/* Logo and Welcome */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box
                component="img"
                src="/favicon.png"
                alt="SupportSpace"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                SupportSpace
              </Typography>
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'white',
                mb: 1,
                fontSize: { xs: '1.5rem', md: '2rem' },
              }}
            >
              Welcome to SupportSpace
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.9rem', md: '1rem' } }}>
              Let's get you set up in just a few steps
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<GradientConnector />}
            sx={{
              mb: { xs: 3, md: 4 },
              '& .MuiStepLabel-label': {
                color: 'rgba(255,255,255,0.5)',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                mt: 1,
                '&.Mui-active': { color: 'white', fontWeight: 600 },
                '&.Mui-completed': { color: 'rgba(255,255,255,0.7)' },
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={GradientStepIcon}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Main Card */}
          <Card
            sx={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              {/* Step 1: Create Organization */}
              {activeStep === 0 && (
                <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <Business sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Create Your Organization
                    </Typography>
                    <Typography color="text.secondary">
                      This is where all your projects and team members will live.
                    </Typography>
                  </Box>

                  <TextField
                    {...register('name')}
                    label="Organization Name"
                    placeholder="e.g., Acme Inc"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    sx={{ mb: 3 }}
                  />

                  <FormControl fullWidth error={!!errors.billingCountry} sx={{ mb: 3 }}>
                    <InputLabel>Billing Country</InputLabel>
                    <Select
                      {...register('billingCountry')}
                      label="Billing Country"
                      defaultValue=""
                    >
                      {COUNTRIES.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.billingCountry && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {errors.billingCountry.message}
                      </Typography>
                    )}
                  </FormControl>

                  {isNZBased && (
                    <Alert
                      severity="info"
                      sx={{
                        mb: 3,
                        bgcolor: 'rgba(59, 130, 246, 0.1)',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                      }}
                    >
                      As a New Zealand based organization, 15% GST will be added to paid plans.
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)',
                      fontSize: '1rem',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                        boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      'Create Organization'
                    )}
                  </Button>
                </Box>
              )}

              {/* Step 2: Install Widget */}
              {activeStep === 1 && (
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <Code sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Install the Widget
                    </Typography>
                    <Typography color="text.secondary">
                      Add this code snippet to your website to enable feedback collection.
                    </Typography>
                  </Box>

                  {/* Widget features */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2.5,
                      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                      What your users can do:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {widgetFeatures.map((feature, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {feature.icon}
                          <Typography variant="body2" color="text.secondary">
                            {feature.text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 1 }}>
                    Installation Code
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      bgcolor: '#0f172a',
                      color: '#e2e8f0',
                      p: 2,
                      borderRadius: 2,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      mb: 2,
                      overflow: 'auto',
                    }}
                  >
                    <Button
                      size="small"
                      onClick={handleCopyCode}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        minWidth: 'auto',
                        color: copied ? '#10b981' : 'rgba(255,255,255,0.6)',
                        '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                      }}
                    >
                      <ContentCopy sx={{ fontSize: 18, mr: 0.5 }} />
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                    <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {`<script src="https://widget.supportspace.io/v1/widget.js"
  data-project="${createdOrg?.id || 'PROJECT_ID'}">
</script>`}
                    </Box>
                  </Box>

                  <Alert
                    severity="info"
                    sx={{
                      mb: 3,
                      bgcolor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                    }}
                  >
                    Paste this code just before the closing <code>&lt;/body&gt;</code> tag on your website.
                  </Alert>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => setActiveStep(2)}
                      sx={{
                        flex: 1,
                        py: 1.5,
                        borderColor: '#e2e8f0',
                        color: 'text.secondary',
                        '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
                      }}
                    >
                      Skip for now
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setActiveStep(2)}
                      sx={{
                        flex: 1,
                        py: 1.5,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                        },
                      }}
                    >
                      I've installed it
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Step 3: Complete */}
              {activeStep === 2 && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)',
                    }}
                  >
                    <Celebration sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    You're all set!
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                    Your organization is ready. Start collecting feedback and managing support from your new dashboard.
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleFinish}
                    sx={{
                      px: 6,
                      py: 1.5,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)',
                      fontSize: '1rem',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                        boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Go to Dashboard
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Footer note */}
          <Typography
            sx={{
              textAlign: 'center',
              mt: 3,
              color: 'rgba(255,255,255,0.4)',
              fontSize: '0.8rem',
            }}
          >
            Need help? Contact support@supportspace.io
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
